"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useDispatch, useSelector } from "react-redux";
import { mockLogin, selectCustomerSession } from "@/store/customerSessionSlice";
import type { AppDispatch } from "@/store";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Alert,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  Divider,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockIcon from "@mui/icons-material/Lock";

/**
 * New portal login layout replicating styling approach of SCS.Web SignIn (split screen, branding on the left).
 * We intentionally do NOT include reCAPTCHA (not currently installed in this project) and reuse existing mockLogin flow.
 */
export default function PortalSplitLogin() {
  const brandColor = "#ffc125";
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error } = useSelector(selectCustomerSession);

  const router = useRouter();
  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down("md"));

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Removed auto-redirect so user always sees the login screen and only navigates
  // to the dashboard after explicitly submitting credentials.

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    const ok = await dispatch(mockLogin({ email, password }))
      .unwrap()
      .then(() => true)
      .catch(() => false);
    if (ok) router.replace("/dashboard");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        bgcolor: theme.palette.background.default,
      }}
    >
      {/* Left brand section */}
      <Box
        sx={{
          flex: "1 1 50%",
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          bgcolor: brandColor,
          color: "#1d1d1d",
          overflow: "hidden",
          p: 4,
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            width: "120px",
            height: "100%",
            background: `linear-gradient(45deg, ${brandColor} 49%, ${theme.palette.background.paper} 51%)`,
            transform: "translateX(60px)",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            textAlign: "center",
            maxWidth: 420,
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 2,
              textShadow: "0 4px 8px rgba(0,0,0,0.45)",
              letterSpacing: "0.5px",
            }}
          >
            Space Coast Management
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 300,
              opacity: 0.95,
              lineHeight: 1.5,
              maxWidth: 360,
              mx: "auto",
              textShadow: "0 3px 6px rgba(0,0,0,0.45)",
            }}
          >
            Complete Enterprise Management System for your business success
          </Typography>
        </Box>
      </Box>

      {/* Right form section */}
      <Box
        sx={{
          flex: "1 1 50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          bgcolor: theme.palette.background.paper,
          p: { xs: 3, md: 6 },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "120px",
            height: "100%",
            background: `linear-gradient(45deg, ${brandColor} 49%, ${theme.palette.background.paper} 51%)`,
            transform: "translateX(-60px)",
          },
        }}
      >
        <Card
          elevation={4}
          sx={{
            width: "100%",
            maxWidth: 440,
            borderRadius: 4,
            backdropFilter: "blur(6px)",
            background: "rgba(25,25,25,0.72)",
            border: "1px solid rgba(255,255,255,0.05)",
          }}
        >
          <CardContent sx={{ p: { xs: 4, md: 5 } }}>
            <Box component="form" onSubmit={handleSubmit} noValidate>
              {/* Mobile brand heading */}
              {mdDown && (
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, mb: 0.5, color: brandColor }}
                  >
                    Space Coast Management
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Sign in to your account
                  </Typography>
                </Box>
              )}

              {!mdDown && (
                <Stack spacing={1.2} sx={{ mb: 4 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: brandColor,
                      letterSpacing: ".5px",
                    }}
                  >
                    Sign In
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", lineHeight: 1.5 }}
                  >
                    Please sign in to your account
                  </Typography>
                  <Divider
                    flexItem
                    sx={{ borderColor: "rgba(255,255,255,0.08)" }}
                  />
                </Stack>
              )}
              <Stack spacing={2.4}>
                <TextField
                  fullWidth
                  required
                  id="email"
                  name="email"
                  label="Email Address"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <EmailIcon sx={{ color: brandColor }} />
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{
                    sx: {
                      bgcolor: "#2a2a2a",
                      borderRadius: 1,
                      color: "#d5d8dc",
                      fontSize: "0.85rem",
                      fontWeight: 500,
                      lineHeight: 1.2,
                      px: 0.75,
                      top: "50%",
                      transform: "translate(16px, -50%) scale(1)",
                      "&.MuiInputLabel-shrink": {
                        top: 0,
                        transform: "translate(14px, -8px) scale(0.75)",
                        bgcolor: "transparent",
                        px: 0,
                      },
                      "&.Mui-focused": { color: brandColor },
                      "&.Mui-error": { color: "#ff6f6f" },
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      bgcolor: "#2a2a2a",
                      transition: "all .25s",
                      "& fieldset": { borderColor: "#3d3d3d" },
                      "&:hover fieldset": { borderColor: "#505050" },
                      "&:hover": { bgcolor: "#303030" },
                      "&.Mui-focused fieldset": {
                        borderColor: brandColor,
                        boxShadow: `0 0 0 2px ${brandColor}40`,
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: brandColor },
                  }}
                />
                <TextField
                  fullWidth
                  required
                  id="password"
                  name="password"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword((s) => !s)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{
                          mr: 2,
                          ml: 0.25,
                          "& .MuiSvgIcon-root": {
                            fontSize: 20,
                            color: "action.active",
                          },
                        }}
                      >
                        <LockIcon />
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{
                    sx: {
                      bgcolor: "#2a2a2a",
                      borderRadius: 1,
                      color: "#d5d8dc",
                      fontSize: "0.85rem",
                      fontWeight: 500,
                      lineHeight: 1.2,
                      px: 0.75,
                      top: "50%",
                      transform: "translate(16px, -50%) scale(1)",
                      "&.MuiInputLabel-shrink": {
                        top: 0,
                        transform: "translate(14px, -8px) scale(0.75)",
                        bgcolor: "transparent",
                        px: 0,
                      },
                      "&.Mui-focused": { color: brandColor },
                      "&.Mui-error": { color: "#ff6f6f" },
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      bgcolor: "#2a2a2a",
                      transition: "all .25s",
                      "& fieldset": { borderColor: "#3d3d3d" },
                      "&:hover fieldset": { borderColor: "#505050" },
                      "&:hover": { bgcolor: "#303030" },
                      "&.Mui-focused fieldset": {
                        borderColor: brandColor,
                        boxShadow: `0 0 0 2px ${brandColor}40`,
                      },
                    },
                    "& .MuiOutlinedInput-root.MuiInputBase-adornedStart .MuiOutlinedInput-input":
                      {
                        paddingLeft: "6px",
                      },
                    "& .MuiInputLabel-root.Mui-focused": { color: brandColor },
                  }}
                />
                {error && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {error}
                  </Alert>
                )}
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    py: 1.6,
                    mt: 0.5,
                    borderRadius: 999,
                    fontWeight: 600,
                    letterSpacing: ".4px",
                    fontSize: "0.95rem",
                    textTransform: "none",
                    bgcolor: brandColor,
                    color: "#1d1d1d",
                    boxShadow: "0 4px 16px -2px rgba(0,0,0,0.4)",
                    "&:hover": {
                      bgcolor: "#f0b515",
                      boxShadow: "0 6px 22px -2px rgba(0,0,0,0.55)",
                    },
                    "&:active": { transform: "translateY(1px)" },
                  }}
                >
                  {loading ? "Signing In..." : "Sign In"}
                </Button>
                <Typography
                  variant="body2"
                  sx={{ textAlign: "center", color: "text.secondary", mt: 0.5 }}
                >
                  Forgot your password? Contact Support.
                </Typography>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
